import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Flex from '../Flex'
import { themeColor, themeFontSize, themeFontWeight } from '../../util/themeLenses'

const getProgressColor = ({disabled}) => themeColor(disabled ? 'lightGray60' : 'accent')
const getThumbColor = ({disabled}) => themeColor(disabled ? 'lightGray60' : 'white')
const getThumbHoverColor = ({disabled}) => themeColor(disabled ? 'lightGray60' : 'accent')
const SLIDER_OPTION_WIDTH = '24px'
const RangeBar = styled.input`
    -webkit-appearance: none;
    background: transparent;
    height: ${SLIDER_OPTION_WIDTH};
    position: relative;
    width: 100%;
    z-index: 1;
    cursor: ${({disabled}) => disabled ? 'not-allowed' : 'pointer'};
    &::-moz-range-track {
        background: linear-gradient(to right, ${getProgressColor} ${({progress}) => progress}%, ${themeColor('gray20')} ${({progress}) => progress}%);
        height: 4px;
        border-radius: 2px;
    }

    &::before {
        content: ' ';
        background: linear-gradient(to right, ${getProgressColor} ${({progress}) => progress}%, ${themeColor('gray20')} ${({progress}) => progress}%);
        height: 4px;
        border-radius: 2px;
        width: 100%;
        position: absolute;
        top: 11px;
        z-index: -1;
    }
    &::-moz-range-thumb {
        appearance: none;
        height: ${SLIDER_OPTION_WIDTH};
        width: ${SLIDER_OPTION_WIDTH};
        border-radius: 50%;
        border: 0;
        background: ${getThumbColor};
        box-shadow: 0 1px 3px rgba(0,0,0,.3);
    }
    &::-webkit-slider-thumb {
        -webkit-appearance: none; /* Override default look */
        appearance: none;
        height: ${SLIDER_OPTION_WIDTH};
        width: ${SLIDER_OPTION_WIDTH};
        border-radius: 50%;
        background: ${getThumbColor};
        box-shadow: 0 1px 3px rgba(0,0,0,.3);
    }

    &:hover::-moz-range-thumb,
    &:focus::-moz-range-thumb {
        border: 2px solid ${getThumbHoverColor};
        background: ${getThumbColor};
    }
    &:focus::-webkit-slider-thumb,
    &:hover::-webkit-slider-thumb {
        border: 2px solid ${getThumbHoverColor};
        background: ${getThumbColor};
    }
`
const RangeDataListOption = styled.div`
    -webkit-appearance: none; /* Override default look */
    padding: 0;
    width:  ${SLIDER_OPTION_WIDTH};
    display: block;
    font-size: ${({ fontSize }) => themeFontSize(fontSize)};
    font-weight: ${({ fontWeight }) => themeFontWeight(fontWeight)};
    color: ${({ fontColor }) => themeColor(fontColor)};
    height: auto;
    text-align: center;
`

RangeDataListOption.propTypes = {
    fontSize: PropTypes.any,
    fontWeight: PropTypes.any,
}

const calculateDefaultVal = (defaultValue, options) => {
    let val = null
    options && options.map((option, index) => {
        if (option.value == defaultValue) {
            val =  index + 1
            return
        }
    })
    return val
}

const Scale = ({
    id,
    label,
    options,
    onChange,
    onFocus,
    value: defaultValue,
    fontWeight,
    fontColor,
    fontSize,
    disabled,
    ...restProps
}) => {
    const dataListId = `${id}-scale-values`
    const firstValue = options.length ? options[0].value : undefined
    const [ val, setVal ] = useState(calculateDefaultVal(defaultValue, options) || firstValue)
    useEffect(() => {
        options && val && onChange(options[val - 1].value)
    }, [val, onChange])
    return <>
        <label htmlFor={id}>{label}</label>
        <Flex margin='2px' dataTest={dataListId} justifyContent='space-between'>
            {options.map((option, number) => <RangeDataListOption
              key={`${dataListId}-item-${number}`}
              fontWeight={fontWeight}
              fontSize={fontSize}
              fontColor={fontColor}
              onClick={() => setVal(number + 1)}
              data-test={`${dataListId}-item-${number}`}
            >{option.label}</RangeDataListOption>)}
        </Flex>
        <RangeBar
          {...restProps}
          type="range"
          min="1"
          max={options.length}
          step="1"
          id={id}
          progress={val ? (100*(val-1)/(options.length-1)) : 0}
          onFocus={onFocus}
          list={dataListId}
          value={val}
          disabled={disabled}
          onChange={(value) => value && value.target && setVal(value.target.value)}
         />
    </>
}

Scale.defaultProps = {
    onChange: () => {},
    options: [],
    value: undefined,
    fontSize: 'medium',
    fontWeight: 'regular',
    fontColor: 'black',
    disabled: false,
}

Scale.propTypes = {
    fontSize: PropTypes.any,
    fontWeight: PropTypes.any,
    fontColor: PropTypes.any,
    id: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any,
        label: PropTypes.node,
    })).isRequired,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.any,
    label: PropTypes.node,
    disabled: PropTypes.bool,
}

export default Scale
